import { useState } from 'react';
import type { Level } from '../core/hp/types';
import { Icon } from './Icon';
import { ActionMenu, type MenuItem } from './Dropdown';
import { levelLabel, store } from '../state/store';
import { freeFloorSlots, isRoofLevel } from '../state/roofRooms';
import { useStore } from './useStore';
import { t } from '../i18n/I18n';

/**
 * The header's floor menu (Figma «Floors», node 412:110538; «Floors · Long list», 985:15065): the levels bottom to top
 * with a drag handle each, the current one ticked, a pencil and a copy on the hovered row, and under a line «Добавить
 * этаж» / «Добавить подвал». At the app's limit (AddLevel.FreeFloorSlots) the copy and the two rows are gone.
 * With `onRemove` and more than one floor the row also has a bin, as the app's level options have «Удалить»
 * (LevelsList.ShowLevelOptions); the caller confirms and removes. The design has no bin: it is the app's action.
 * A project opened only to look at lists its floors to switch between and nothing else.
 */
export function FloorSelector({ levels, value, onChange, onRemove, className = '' }: {
  levels: readonly Level[]; value: number; onChange(value: number): void; onRemove?(index: number): void; className?: string;
}) {
  const editable = useStore(state => state.edit === 'all');
  const [renaming, setRenaming] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const current = levels[value] ? value : 0, room = freeFloorSlots(levels) > 0;
  const label = (index: number) => levelLabel(levels, index);
  const commitRename = () => {
    if (renaming !== null) {
      const name = draft.trim();
      if (name && name !== label(renaming)) store.renameLevel(renaming, name);
    }
    setRenaming(null);
  };
  const items: MenuItem[] = [
    ...levels.map((_, index): MenuItem => ({
      id: `level-${index}`, label: label(index), checked: index === value, handle: editable, keepOpen: renaming === index,
      content: renaming === index ? <input className="dropdown-rename" autoFocus value={draft} aria-label={t('#EditorFloorName')} onChange={event => setDraft(event.target.value)} onBlur={commitRename}
        onKeyDown={event => { event.stopPropagation(); if (event.key === 'Enter') { event.preventDefault(); commitRename(); } else if (event.key === 'Escape') { event.preventDefault(); setRenaming(null); } }} /> : undefined,
      trailing: editable && <>
        <button type="button" aria-label={t('#EditorRenameFloorLabel', label(index))} data-tip={t('Rename')} data-tip-side="above" onClick={() => { setDraft(label(index)); setRenaming(index); }}><Icon name="pencil" size={19} /></button>
        {room && !isRoofLevel(levels[index]) && <button type="button" aria-label={t('#EditorDuplicateFloorLabel', label(index))} data-tip={t('#WebDuplicate')} data-tip-side="above" onClick={() => store.duplicateLevel(index)}><Icon name="copyDoc" size={19} /></button>}
        {onRemove && levels.length > 1 && <button type="button" aria-label={t('#EditorDeleteFloorRowLabel', label(index))} data-tip={t('Delete')} data-tip-side="above" onClick={() => onRemove(index)}><Icon name="trash" size={19} /></button>}
      </>,
      onSelect: () => { if (renaming === null) onChange(index); },
    })),
    ...(editable && room ? [
      { id: 'add-separator', separator: true },
      { id: 'add-floor', label: t('#AddLevel'), className: 'is-action floor-add', icon: <Icon name="plus" size={19} />, onSelect: () => store.addLevel('above') },
      { id: 'add-basement', label: t('#AddLowerLevel'), className: 'is-action floor-add', icon: <Icon name="plus" size={19} />, onSelect: () => store.addLevel('basement') },
    ] satisfies MenuItem[] : []),
  ];
  // Under a finger every row shows its three 32 px buttons and the tick together: the card widens to keep the name.
  const width = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches ? 288 : 240;
  return <ActionMenu label={t('#EditorFloor')} tip={t('#WebTipFloorOptions')} handleTip={t('#WebTipDragToReorder')} className={`floor-picker ${className}`} triggerClassName="floor-trigger" width={width} align="start"
    trigger={<><Icon name="layers" size={20} /><span>{levels.length ? label(current) : t('#EditorFloor')}</span><Icon name="chevronDown" size={16} /></>}
    items={items} onReorder={editable ? (id, to) => store.moveLevel(Number(id.slice(6)), to) : undefined} onOpenChange={open => { if (!open) setRenaming(null); }} />;
}
